// CareLogix™ ORACLE — Global Intelligence Continuity Acknowledgement Gate (Phase 32.1)
// Strictly additive. Acts as a mandatory pre-access checkpoint for any /oracle* route.
// Storage key: `oracle_acknowledged_v1`. Stays cleanly out of /access and /adminone scopes.
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";
import OraclePrivacyRiskSettings from "@/components/gate/OraclePrivacyRiskSettings";

export const ORACLE_ACK_STORAGE_KEY = "oracle_acknowledged_v1";

function isAcknowledged() {
  if (typeof window === "undefined") return true; // SSR-safe
  try { return window.localStorage.getItem(ORACLE_ACK_STORAGE_KEY) === "true"; }
  catch { return false; }
}

export function setAcknowledged(v) {
  if (typeof window === "undefined") return;
  try {
    if (v) window.localStorage.setItem(ORACLE_ACK_STORAGE_KEY, "true");
    else  window.localStorage.removeItem(ORACLE_ACK_STORAGE_KEY);
  } catch (_) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("oracle:acknowledged-changed", { detail: { value: !!v } })); } catch (_) { /* ignore */ }
}

const BADGES = [
  { label: "LIVE",              color: PALETTE.greenAccent },
  { label: "VERIFIED",          color: PALETTE.gold },
  { label: "HUMAN-IN-THE-LOOP", color: PALETTE.amber },
  { label: "COMPLIANCE LOCK™",  color: PALETTE.emerald },
];

const DOES_NOT = [
  "Guarantee profits",
  "Guarantee investment outcomes",
  "Guarantee market performance",
  "Guarantee grant awards",
  "Guarantee funding approvals",
  "Guarantee startup success",
  "Provide financial advice",
  "Execute trades autonomously",
];

const ASSURANCES = [
  "Confidence Scored",
  "Risk Rated",
  "Assumption Driven",
  "Uncertainty Surfaced",
  "Human Confirmed",
];

const DOMAINS = [
  { icon: "📈", label: "Market Intelligence" },
  { icon: "💰", label: "Funding Intelligence" },
  { icon: "🏛",  label: "Grant Intelligence" },
  { icon: "🌍", label: "Global Opportunity Intelligence" },
  { icon: "🏥", label: "Healthcare Intelligence" },
  { icon: "🚀", label: "Startup Intelligence" },
  { icon: "🤖", label: "Agent Swarm Intelligence" },
  { icon: "📊", label: "Executive Briefings" },
];

export default function OracleAcknowledgement({ onAccept, testid = "oracle-ack" }) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleAccept = () => {
    setAcknowledged(true);
    if (typeof onAccept === "function") {
      try { onAccept(); } catch (_) { /* ignore */ }
    }
  };

  return (
    <main style={S.shell} data-testid={testid} role="dialog" aria-modal="true" aria-labelledby={`${testid}-title`}>
      <div style={S.bgGlow} aria-hidden="true" />
      <motion.section
        style={S.card}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
      >
        <header style={S.header}>
          <span style={S.eyebrow} data-testid={`${testid}-eyebrow`}>GLOBAL INTELLIGENCE CONTINUITY™</span>
          <h1 style={S.title} id={`${testid}-title`} data-testid={`${testid}-title`}>
            CARELOGIX ORACLE™
          </h1>
          <p style={S.subtitle} data-testid={`${testid}-subtitle`}>
            Your Privacy, Security, Risk Awareness, and Decision Integrity Matter.
          </p>
        </header>

        <div style={S.badgeRow} data-testid={`${testid}-badges`}>
          {BADGES.map((b) => (
            <span
              key={b.label}
              style={{ ...S.badge, color: b.color, borderColor: b.color, boxShadow: `0 0 16px ${b.color}33 inset` }}
              data-testid={`${testid}-badge-${b.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              <span style={{ ...S.badgeDot, background: b.color }} aria-hidden="true" />
              {b.label}
            </span>
          ))}
        </div>

        <p style={S.lead} data-testid={`${testid}-lead`}>
          CareLogix ORACLE™ provides market intelligence, research, analytics, forecasting models, grants intelligence, startup
          intelligence, healthcare intelligence, funding intelligence, and global opportunity monitoring.
          <br />
          <strong style={{ color: PALETTE.goldBright }}>ORACLE™ is designed to assist human decision-making.</strong>
        </p>

        <div style={S.twoCol}>
          <div style={S.column} data-testid={`${testid}-does-not-block`}>
            <h3 style={S.colTitle}>ORACLE™ does <strong style={{ color: PALETTE.red }}>NOT</strong>:</h3>
            <ul style={S.list} data-testid={`${testid}-does-not-list`}>
              {DOES_NOT.map((line) => (
                <li style={S.listItem} key={line}>
                  <span style={{ ...S.bullet, color: PALETTE.red }} aria-hidden="true">✕</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>

          <div style={S.column} data-testid={`${testid}-assurances-block`}>
            <h3 style={S.colTitle}>All intelligence is:</h3>
            <ul style={S.list} data-testid={`${testid}-assurances-list`}>
              {ASSURANCES.map((line) => (
                <li style={S.listItem} key={line}>
                  <span style={{ ...S.bullet, color: PALETTE.greenAccent }} aria-hidden="true">✓</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <h3 style={S.sectionTitle} data-testid={`${testid}-domains-title`}>ORACLE MAY PROCESS</h3>
        <ul style={S.domainGrid} data-testid={`${testid}-domains-grid`}>
          {DOMAINS.map((d) => (
            <li style={S.domainPill} key={d.label}>
              <span style={S.domainIcon} aria-hidden="true">{d.icon}</span>
              <span style={S.domainLabel}>{d.label}</span>
            </li>
          ))}
        </ul>

        <div style={S.complianceLock} data-testid={`${testid}-compliance-lock`}>
          <span style={S.complianceTitle}>COMPLIANCE LOCK™</span>
          <ul style={S.complianceList}>
            <li>Past performance is not predictive of future outcomes.</li>
            <li>All outputs are educational, informational, and decision-support intelligence.</li>
            <li>Human judgment remains required.</li>
          </ul>
        </div>

        <div style={S.ctaRow}>
          <button
            type="button"
            onClick={handleAccept}
            style={S.ctaPrimary}
            data-testid={`${testid}-continue`}
            aria-label="Continue to CareLogix ORACLE"
          >
            <span style={{ marginRight: 10 }} aria-hidden="true">🟨</span>
            CONTINUE TO ORACLE™
            <span style={{ marginLeft: 10 }} aria-hidden="true">→</span>
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            style={S.ctaSecondary}
            data-testid={`${testid}-settings`}
            aria-label="Open ORACLE privacy and risk settings"
          >
            <span style={{ marginRight: 8 }} aria-hidden="true">⚙</span>
            ORACLE PRIVACY & RISK SETTINGS
          </button>
        </div>

        <footer style={S.footer}>
          <span style={S.footerLink}>Privacy Policy</span>
          <span style={S.footerSep}>|</span>
          <span style={S.footerLink}>Terms of Service</span>
          <span style={S.footerSep}>|</span>
          <span style={S.footerLink}>Risk Disclosure</span>
          <span style={S.footerSep}>|</span>
          <span style={S.footerLink}>Accessibility</span>
        </footer>

        <div style={S.guard} data-testid={`${testid}-guard`}>
          <span aria-hidden="true">🛡️</span>
          <span>Protected by CareLogix™ ORACLE Intelligence Infrastructure™ · LOGIX-2™ Agent Swarm Active · Human-In-The-Loop Decision Framework Enabled</span>
        </div>
      </motion.section>

      <OraclePrivacyRiskSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        testid={`${testid}-privacy-risk-drawer`}
      />
    </main>
  );
}

export { isAcknowledged };

const S = {
  shell: {
    position: "fixed",
    inset: 0,
    zIndex: 9990,
    background: "radial-gradient(circle at 50% 0%, rgba(15,45,30,0.55) 0%, rgba(4,6,8,1) 65%)",
    overflowY: "auto",
    padding: "24px 16px 48px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
  },
  bgGlow: {
    position: "absolute",
    inset: 0,
    backgroundImage: "radial-gradient(ellipse 600px 320px at 50% 6%, rgba(197,160,89,0.10), transparent 65%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: 920,
    background: "linear-gradient(180deg, rgba(11,15,20,0.96), rgba(6,18,12,0.96))",
    border: `1px solid ${PALETTE.goldGlow}`,
    borderRadius: 24,
    padding: "32px clamp(20px, 4vw, 44px) 28px",
    color: PALETTE.slate3,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    boxShadow: "0 30px 80px rgba(0,0,0,0.55), inset 0 0 60px rgba(197,160,89,0.05)",
  },
  header: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 6 },
  eyebrow: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.32em" },
  title: { margin: 0, color: PALETTE.goldBright, fontSize: "clamp(24px, 4.2vw, 38px)", letterSpacing: "0.10em", fontWeight: 800 },
  subtitle: { margin: 0, color: PALETTE.slate3, fontSize: "clamp(13px, 1.6vw, 16px)", maxWidth: 640, lineHeight: 1.5 },
  badgeRow: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 },
  badge: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "rgba(11,15,20,0.7)",
    border: "1px solid",
    padding: "6px 12px",
    borderRadius: 999,
    fontFamily: "'Space Mono', monospace",
    fontSize: 10.5,
    letterSpacing: "0.14em",
  },
  badgeDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block" },
  lead: { margin: "4px 0 8px", textAlign: "center", lineHeight: 1.6, fontSize: 14.5, color: PALETTE.slate3 },
  twoCol: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 },
  column: { background: "rgba(4,6,8,0.6)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", gap: 8 },
  colTitle: { margin: 0, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: "0.10em" },
  list: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  listItem: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5, lineHeight: 1.45, color: PALETTE.slate3 },
  bullet: { fontFamily: "'Space Mono', monospace", fontSize: 13, width: 14, textAlign: "center" },
  sectionTitle: { margin: "8px 0 0", color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.16em", textAlign: "center" },
  domainGrid: { margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 },
  domainPill: { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(197,160,89,0.05)", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 12 },
  domainIcon: { fontSize: 18, lineHeight: 1 },
  domainLabel: { fontSize: 13, color: PALETTE.slate3 },
  complianceLock: { marginTop: 4, background: "rgba(252,211,77,0.05)", border: `1px solid ${PALETTE.amber}`, borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", gap: 8 },
  complianceTitle: { color: PALETTE.amber, fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: "0.18em", fontWeight: 800, textAlign: "center" },
  complianceList: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4, color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.45 },
  ctaRow: { display: "flex", flexDirection: "column", gap: 8, marginTop: 6 },
  ctaPrimary: {
    appearance: "none",
    width: "100%",
    background: "linear-gradient(180deg, #fde68a 0%, #c5a059 100%)",
    color: "#0b0f14",
    border: "1px solid #c5a059",
    boxShadow: "0 12px 30px rgba(197,160,89,0.30), inset 0 -2px 0 rgba(0,0,0,0.15)",
    padding: "14px 18px",
    borderRadius: 14,
    fontWeight: 800,
    fontFamily: "'Space Mono', monospace",
    letterSpacing: "0.14em",
    fontSize: 14,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ctaSecondary: {
    appearance: "none",
    width: "100%",
    background: "transparent",
    color: PALETTE.gold,
    border: `1px solid ${PALETTE.goldGlow}`,
    padding: "12px 16px",
    borderRadius: 14,
    fontWeight: 700,
    fontFamily: "'Space Mono', monospace",
    letterSpacing: "0.12em",
    fontSize: 12.5,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  footer: { marginTop: 6, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, color: PALETTE.slate4, fontSize: 11.5 },
  footerLink: { textDecoration: "underline dotted", cursor: "pointer", color: PALETTE.gold },
  footerSep: { color: "rgba(255,255,255,0.18)" },
  guard: { display: "flex", justifyContent: "center", alignItems: "center", gap: 8, color: PALETTE.slate5, fontSize: 10.5, lineHeight: 1.4, textAlign: "center", marginTop: 4 },
};
